import { FormText, IconButton } from "@itk/mui-components";
import { useForm } from "@itk/react-form";
import { Box, CircularProgress, InputAdornment, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Model_QuickSearchGet, Model_QuickSearchPost, Shared_QuickSearchKinds } from "src/backend/openapi/v1";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useEffectAfterMount } from "src/utils/hooks/useEffectAfterMount";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { globalSearchActions } from "./global-search-store";
import { DekoxIcon } from "../../../deko-x-icons";
import { GlobalSearchRow } from "./components/global-search-row";
import { useGlobalSearchDialogT } from "src/translation-hooks/global-search-dialog";
import useOutsideClick from "src/utils/hooks/useOutsideClick";

const mapStateToProps = (state: RootState) => ({
  globalSearchState: state.generalReducer.globalSearchReducer.globalSearchState
});

const ownActions = {
  globalSearchRequest: globalSearchActions.globalSearchRequest,
  globalSearchReset: globalSearchActions.globalSearchReset
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const GlobalSearchBase = (props: Props) => {
  const { globalSearchRequest, globalSearchState, globalSearchReset } = props;

  const theme = useTheme();

  const globalSearchRef = useOutsideClick(() => {
    setShowPopover(false);
  });

  const genT = useGenericTranslationsT();
  const globalSearchDialogT = useGlobalSearchDialogT();

  const initialState: Model_QuickSearchPost = useMemo(() => {
    return {
      wildCardSearch: ""
    };
  }, []);

  const { formState, formController, resetChanges } = useForm(initialState);

  const [showPopover, setShowPopover] = useState(false);

  useEffect(() => {
    return () => {
      globalSearchReset();
    };
  }, []);

  useEffectAfterMount(() => {
    if (formState.wildCardSearch.value) {
      globalSearchRequest({ wildCardSearch: formState.wildCardSearch.value });
    }
  }, [formState.wildCardSearch.value]);

  const groupedGlobalSearch = useMemo(() => {
    if (globalSearchState.loading === "has-loaded") {
      return globalSearchState.data.reduce(
        (acc, item) => {
          if (acc[item.quickSearchKind]) acc[item.quickSearchKind].push(item);
          else acc[item.quickSearchKind] = [item];
          return acc;
        },
        {} as Record<Shared_QuickSearchKinds, Model_QuickSearchGet[]>
      );
    }
    return [];
  }, [globalSearchState]);

  const quickSearchKindLabel = (key: Shared_QuickSearchKinds) => {
    switch (key) {
      case Shared_QuickSearchKinds.Product:
        return globalSearchDialogT.product;
      default:
        return "";
    }
  };

  return (
    <>
      <Box ref={globalSearchRef} maxWidth={500} width="100%">
        <FormText
          width="100%"
          t={genT}
          k={genericTranslationsK}
          field="dynamicValue"
          formFieldState={formState.wildCardSearch}
          formFieldController={formController.wildCardSearch}
          placeholder={globalSearchDialogT.searchPlaceholder}
          autoComplete="global-search"
          onFocus={() => {
            setShowPopover(true);
          }}
          sx={{
            "& .MuiInputBase-root": {
              background: theme.palette.customColors.lightGrey,
              border: `1px solid ${theme.palette.customColors.grey}`,
              borderRadius: 1,
              overflow: "hidden"
            },
            "& .MuiInputBase-input": {
              padding: 0
            },
            "& .MuiInputBase-root .MuiInputBase-input": {
              height: 40
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {globalSearchState.loading === "is-loading" && formState.wildCardSearch.value ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DekoxIcon icon="search" sx={{ color: theme.palette.customColors.darkGrey }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {formState.wildCardSearch.value && (
                    <IconButton k={genericTranslationsK.dynamicValue} onClick={() => resetChanges()}>
                      <DekoxIcon fontSize="small" icon="close" />
                    </IconButton>
                  )}
                </InputAdornment>
              )
            }
          }}
        />
        {globalSearchState.loading === "has-loaded" && formState.wildCardSearch.value && showPopover && (
          <Stack
            position="absolute"
            borderRadius={2}
            top={50}
            zIndex={9999}
            border={`1px solid ${theme.palette.customColors.border}`}
            boxShadow={2}
            overflow="hidden"
            bgcolor={theme.palette.customColors.white}
            width={globalSearchRef.current?.clientWidth}
          >
            {Object.values(groupedGlobalSearch).every(x => x.length === 0) && formState.wildCardSearch.value && (
              <Typography variant="body1" textAlign="center" py={3} px={2}>
                {globalSearchDialogT.nothingFound}
              </Typography>
            )}
            {Object.keys(groupedGlobalSearch).map(k => {
              const key = Number(k) as Shared_QuickSearchKinds;
              return (
                <Stack key={key}>
                  <Typography variant="subtitle2" px={2} py={1}>
                    {quickSearchKindLabel(key)}
                  </Typography>
                  {groupedGlobalSearch[key].map(data => {
                    return (
                      <GlobalSearchRow
                        key={data.entityId}
                        data={data}
                        handleClose={() => {
                          resetChanges();
                          setShowPopover(false);
                        }}
                      />
                    );
                  })}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </>
  );
};

export const GlobalSearch = connect(mapStateToProps, ownActions)(GlobalSearchBase);
